import { Hash } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { SlackConnectionFormState } from "./slack-connection-config";

export function SlackConnectionFields({
  value,
  onChange,
}: {
  value: SlackConnectionFormState;
  onChange: (value: SlackConnectionFormState) => void;
}) {
  const update = <K extends keyof SlackConnectionFormState>(
    key: K,
    nextValue: SlackConnectionFormState[K],
  ) => {
    onChange({ ...value, [key]: nextValue });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Authentication */}
      <Card>
        <CardHeader>
          <CardTitle>Authentication</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FormField label="Bot Token">
            <input
              type="password"
              value={value.botToken}
              onChange={(e) => update("botToken", e.target.value)}
              placeholder="xoxb-your-bot-token"
              className="form-input font-mono"
            />
          </FormField>

          <FormField label="App Token">
            <input
              type="password"
              value={value.appToken}
              onChange={(e) => update("appToken", e.target.value)}
              placeholder="xapp-your-app-token"
              className="form-input font-mono"
            />
            <FieldHint text="Required for Socket Mode." />
          </FormField>

          <FormField label="User Token">
            <input
              type="password"
              value={value.userToken}
              onChange={(e) => update("userToken", e.target.value)}
              placeholder="xoxp-..."
              className="form-input font-mono"
            />
            <FieldHint text="Optional. Enables user event visibility." />
          </FormField>
        </CardContent>
      </Card>

      {/* Connection Mode */}
      <Card>
        <CardHeader>
          <CardTitle>Connection Mode</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <FormField label="Mode">
            <select
              value={value.mode}
              onChange={(e) => update("mode", e.target.value as "socket" | "http")}
              className="form-input"
            >
              <option value="socket">Socket Mode</option>
              <option value="http">HTTP</option>
            </select>
          </FormField>

          {value.mode === "http" && (
            <FormField label="Signing Secret">
              <input
                type="password"
                value={value.signingSecret}
                onChange={(e) => update("signingSecret", e.target.value)}
                placeholder=""
                className="form-input font-mono"
              />
              <FieldHint text="Required for HTTP webhook verification." />
            </FormField>
          )}

          <FormField label="Webhook Path">
            <input
              value={value.webhookPath}
              onChange={(e) => update("webhookPath", e.target.value)}
              placeholder="/slack/events"
              className="form-input font-mono"
            />
            <FieldHint text="Override the default webhook endpoint path." />
          </FormField>
        </CardContent>
      </Card>
    </div>
  );
}

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: "var(--text-strong)" }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function FieldHint({ text }: { text: string }) {
  return (
    <p className="text-[11px]" style={{ color: "var(--muted-foreground)" }}>
      {text}
    </p>
  );
}
